import {Injectable} from '@angular/core';
import {Employees, EmpWeekPlan, EmpWorkPlan} from './time-app/models/employees-model';
import {IndexedDbData} from './time-app/models/indexedDB-model';
import {Orders} from './time-app/models/order-model';
import {Spesen} from './time-app/models/spesen-model';
import {CostCenter, TimeControlEntry} from './time-app/models/timeControlEntry';
import {BenutzerModels} from './models/benutzer-models';
import {UserAuthenticationService} from './services/user-authentication.service';
import {VinavonUpdateService} from './services/vinavon-update.service';
import {compareVersions} from 'compare-versions';
import {ServiceAppModels} from './models/serviceapp-models';
import {Capacitor} from '@capacitor/core';
import {IndexDBController} from './controller/localStorage/IndexDBController';
import {LocalStorageController} from './controller/localStorage/LocalStorageController';
import {IOSPreferenceStorageController} from './controller/localStorage/IOSPreferenceStorageController';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageService {
    private readonly SWISS_WEB_CONFIG_DB_NAME = 'SwissWebConfigDB';
    private userAuthService: UserAuthenticationService | undefined;
    private locked = false;
    private localStorageController: LocalStorageController;

    constructor() {
        /*
         * when using capacitor indexDB is only transient, so restarting the app clears the indexDB
         * for android this is easily solved by calling storage persist function
         * ios required the usage of their preferences api
         */
        this.localStorageController = Capacitor.getPlatform() == 'ios' ? new IOSPreferenceStorageController() : new IndexDBController();
        if (navigator.storage && navigator.storage.persist) {
            navigator.storage.persist();
        }
    }

    public async setUpService(userAuthService: UserAuthenticationService, vinavonUpdateService: VinavonUpdateService): Promise<void> {
        this.userAuthService = userAuthService;
        // has been used before so migration could be needed
        let migrated = await this.get('migrated', this.SWISS_WEB_CONFIG_DB_NAME, true, '1.0');
        if (migrated === true || migrated === false) {
            migrated = '2.0';
        }
        if (migrated != '1.0') {
            const a = compareVersions(vinavonUpdateService.vinavonVersion, migrated);
            if (a === 1) {
                try {
                    await this.migrateData(migrated);
                    await this.add('migrated', vinavonUpdateService.vinavonVersion, this.SWISS_WEB_CONFIG_DB_NAME, false, true);
                } catch (e) {
                    return;
                }
            } else if (a === -1) {
                // downgrade happend
                console.log('System was downgraded _ you should delete your local storage');
                return;
            } else {
                return;
            }
        }
        return;
    }

    private async migrateData(prevVersion): Promise<boolean> {
        let migrationList: any[] = [];
        let migrateDocuments = false;
        let v = compareVersions('2.14.13', prevVersion);
        if (v == 1) {
            migrateDocuments = true;

            migrationList = migrationList.concat([// SwissWebMainDB
                {table: 'SwissWebMainDB', key: 'adressTypen'},
                {table: 'SwissWebMainDB', key: 'feldKonfigurationen'},
                {table: 'SwissWebMainDB', key: 'kalender'},
                {table: 'SwissWebMainDB', key: 'last_selected_schedule_employees'},
                {table: 'SwissWebMainDB', key: 'last_used_appointment'},
                {table: 'SwissWebMainDB', key: 'mitarbeiter'},
                {table: 'SwissWebMainDB', key: 'mitarbeitergruppen'},
                {table: 'SwissWebMainDB', key: 'mitarbeitergruppenzuord'},
                {table: 'SwissWebMainDB', key: 'offlineObjektBaum'},
                {table: 'SwissWebMainDB', key: 'offlineObjektBezeichnungen'},
                {table: 'SwissWebMainDB', key: 'offlineObjektTypen'},
                {table: 'SwissWebMainDB', key: 'orderPositions'},
                {table: 'SwissWebMainDB', key: 'suppliers'}, {table: 'SwissWebMainDB', key: 'terminAdressen'},
                {table: 'SwissWebMainDB', key: 'terminkategorien'},

                {table: this.SWISS_WEB_CONFIG_DB_NAME, key: 'SwissAppUserConfig'},
                {table: this.SWISS_WEB_CONFIG_DB_NAME, key: 'anwender'},
                {table: this.SWISS_WEB_CONFIG_DB_NAME, key: 'benutzerRechte'},
                {table: this.SWISS_WEB_CONFIG_DB_NAME, key: 'userAuthenticated'},

                // SwissTimeEntry
                {table: 'SwissTimeEntry', key: 'allEmployees'},
                {table: 'SwissTimeEntry', key: 'costCenterObjektrange'},
                {table: 'SwissTimeEntry', key: 'costCenters'},
                {table: 'SwissTimeEntry', key: 'currentTimeEntry'},
                {table: 'SwissTimeEntry', key: 'deleteTimeEntry'},
                {table: 'SwissTimeEntry', key: 'myTimeControlEntries'},
                {table: 'SwissTimeEntry', key: 'plannedEmployeeWeeks'},
                {table: 'SwissTimeEntry', key: 'plannedEmployeeWorks'},

                // SwissInternDB
                {table: 'SwissInternDB', key: 'rapport_header_logo'},

                // ServiceAppDB
                {table: 'ServiceAppDB', key: 'adressen'},
                {table: 'ServiceAppDB', key: 'artikel'},
                {table: 'ServiceAppDB', key: 'last_used_appointment'},
                {table: 'ServiceAppDB', key: 'checkListenTemplates'},
                {table: 'ServiceAppDB', key: 'objektAdressen'},
                {table: 'ServiceAppDB', key: 'objektDokumente'},
                {table: 'ServiceAppDB', key: 'objektKategorien'},
                {table: 'ServiceAppDB', key: 'objekte'},
                {table: 'ServiceAppDB', key: 'produkte'},
                {table: 'ServiceAppDB', key: 'rapport_header_logo'},
                {table: 'ServiceAppDB', key: 'sa_config'},
                {table: 'ServiceAppDB', key: 'servicePositionen'},
                {table: 'ServiceAppDB', key: 'stammdaten'},
                {table: 'ServiceAppDB', key: 'standardObjekt'},
                {table: 'ServiceAppDB', key: 'terminDokumentZuord'},
                {table: 'ServiceAppDB', key: 'zeiten'},
                {table: 'ServiceAppDB', key: 'timeControlEntries'},

                // ServiceAppDB_Doku
                // keys are dynamic so get all keys and migrate on its own
            ]);
        }


        return new Promise((resolve, reject) => {
            return this.get('anwender', this.SWISS_WEB_CONFIG_DB_NAME, true).then((anwender: BenutzerModels.Anwender) => {
                if (anwender) {
                    const promises: Promise<any>[] = [];
                    migrationList.forEach(entry => {
                        promises.push(new Promise<void>((resolve, reject) => {
                            this.get(entry.key, entry.table, true, new Error('data not found')).then(val => {
                                if (val instanceof Error) {
                                    if (val.message == 'data not found') {
                                        resolve();
                                    } else {
                                        reject();
                                    }
                                } else {
                                    this.add(entry.key + '_' + anwender.mandatorID + '_' + anwender.employeeID, val, entry.table, true, true).then(() => resolve()).catch((reason) => reject(reason));
                                }
                                // delete org entry after add? Σ(-᷅_-᷄๑)
                            }).catch(reason => {
                                if (reason == 1) {
                                    resolve();
                                } else {
                                    reject();
                                }
                            });
                        }));
                    });

                    if (migrateDocuments) {
                        this.getAllKeys('ServiceAppDB_Doku').then((keys: any[]) => {
                            keys.forEach(key => {
                                promises.push(new Promise<void>((resolve, reject) => {
                                    this.get(key, 'ServiceAppDB_Doku', true).then(val => {
                                        this.add(key + '_' + anwender.mandatorID + '_' + anwender.employeeID, val, 'ServiceAppDB_Doku', true, true).then(() => resolve()).catch(reason => reject(reason));
                                        // delete org entry after add? Σ(-᷅_-᷄๑)
                                    }).catch(reason => {
                                        if (reason == 1) {
                                            // key not in indexDB -> Dont need to migrate
                                            // maybe add mandatory flag? (-_-)ゞ
                                            resolve();
                                        } else {
                                            reject();
                                        }
                                    });
                                }));
                            });
                            Promise.allSettled(promises).then(values => {
                                // we reject if any is rejected so we set i to break
                                for (let i = 0; i < values.length; i++) {
                                    if (values[i].status === 'rejected') {
                                        return reject(1);
                                    }
                                }
                                resolve(true);
                            }).catch(error => {
                                // something went wrong while user specific keys are added
                                console.error(error);
                                reject(1);
                            });
                        });
                    } else {
                        resolve(true);
                    }
                } else {
                    // something wrong no anwender to set
                    reject(2);
                }
            });
        });

    }

    // hinzufügen oder aktualisieren von Daten im Speicher
    public async add(keyName: string, value: any, storagename: string = '', ignoreBlock = false, global = false): Promise<string> {
        keyName = this.evalKey(global, keyName);

        if (this.locked && !ignoreBlock) {
            return new Promise((resolve, reject) => {
                reject('error: db Locked');
            });
        }

        // using capacitor indexDB is only transient, so restarting the app clears the indexDB
        return this.localStorageController.add(storagename, value, keyName);
    }

    // liest gespeicherte Daten aus dem Speicher
    public async get<T = any>(keyName: string, storagename: string, global = false, defaultValue: any = undefined): Promise<T> {
        keyName = this.evalKey(global, keyName);

        if (this.locked) {
            return new Promise((resolve, reject) => {
                reject('error: db Locked');
            });
        }

        return this.localStorageController.get<T>(storagename, keyName, defaultValue);
    }

    public async addToExisting<T>(keyName: string, value: any[], storagename: string = '', ignoreBlock = false, global = false, unique = true): Promise<T> {
        keyName = this.evalKey(global, keyName);

        const currentValue = await this.get(keyName, storagename, true, []);
        let data: any[];
        if (unique && currentValue instanceof Array) {
            const set = new Set(currentValue);
            value.forEach(v => set.add(v));

            data = Array.from(set);
        } else {
            data = currentValue.concat(value);
        }

        await this.add(keyName, data, storagename, ignoreBlock, true);
        return (data as T);
    }

    // liest alle Schlüssel aus einem Speicher aus
    public async getAllKeys(storageName: string): Promise<string[]> {
        if (this.locked) {
            return new Promise((resolve, reject) => {
                reject('error: db Locked');
            });
        }
        return this.localStorageController.getKeys(storageName);
    }

    // löscht Daten aus dem Speicher
    public async delete(keyName: string, storagename: string, global = false): Promise<string> {
        try {
            keyName = this.evalKey(global, keyName);
        } catch (e) {
            return new Promise((resolve, reject) => {
                reject('could not write into db: ' + e + '');
            });
        }

        if (this.locked) {
            return new Promise((resolve, reject) => {
                reject('error: db Locked');
            });
        }

        return this.localStorageController.delete(storagename, keyName);
    }

    /* Zeiterfassungs-App */
    public async getAllDataFromIndexedDB(): Promise<IndexedDbData> {
        let indexDBData: IndexedDbData = {
            orders: [], costs: [], timeEntry: [], spesen: [], allEmployees: []
        };

        return Promise.all([
            this.get('orders', 'SwissTimeEntry', true, []),
            this.get('costCenters', 'SwissTimeEntry', false, []),
            this.get('myTimeControlEntries', 'SwissTimeEntry', false, []),
            this.get('spesen', 'SwissTimeEntry', true, []),
            this.get('allEmployees', 'SwissTimeEntry', false, []),
            this.get('language', 'SwissTimeEntry', true, 'de'),
            this.get('plannedEmployeeWorks', 'SwissTimeEntry', false, []),
            this.get('plannedEmployeeWeeks', 'SwissTimeEntry', false, []),
            this.get('objektKategorien', 'ServiceAppDB', false, [])])
            .then((res) => {
                if (res[0] !== false) {
                    indexDBData.orders = <Orders[]>res[0]!;
                }

                if (res[1] !== false) {
                    indexDBData.costs = <CostCenter[]>res[1]!;
                }

                if (res[2] !== false) {
                    indexDBData.timeEntry = <TimeControlEntry[]>res[2]!;
                }

                if (res[3] !== false) {
                    indexDBData.spesen = <Spesen[]>res[3]!;
                }

                if (res[4] !== false) {
                    indexDBData.allEmployees = <Employees[]>res[4]!;
                }

                if (res[5] !== false) {
                    indexDBData.language = <string>res[5]!;
                }

                if (res[6] !== false) {
                    indexDBData.plannedEmployeeWorks = <EmpWorkPlan[]>res[6]!;
                }

                if (res[7] !== false) {
                    indexDBData.plannedEmployeeWeeks = <EmpWeekPlan[]>res[7]!;
                }

                if (res[8] !== false) {
                    indexDBData.objectCategory = <ServiceAppModels.SA_ObjektKategorie[]>res[8]!;
                }

                return indexDBData;
            })
            .catch((err) => {
                console.error(err);
                return indexDBData;
            });
    }

    /* Get all Data for Backup*/
    async getAllData(): Promise<any> {
        return this.localStorageController.getAllData();
    }

    public lockDb(): void {
        this.locked = true;
    }

    public unlockDB(): void {
        this.locked = false;
    }

    private evalKey(global: boolean, keyName: string): string {
        const userAuth = this.userAuthService;
        if (!global) {
            if (!userAuth) {
                throw new Error('userAuthService not set');
            }
            const user = userAuth.getCurrentAuthenticatedUser();
            if (!user) {
                throw new Error('no user authenticated');
            }
            return keyName + '_' + user.mandatorID + '_' + user.employeeID;
        }

        return keyName;
    }

    async resetLocalStorage(): Promise<void>{
        await this.localStorageController.deleteDatabase();
    }
}
